// App root. Boots by resolving identity (GET /api/admin/me): 401 or 403 → the
// sign-in panel, in the state the refusal names (`resolveSignInReason` reads the
// backend's error code — a 403 is "sign in with Google" far more often than it is
// "this account can never get in"), ok → the routed app. The router only mounts
// once signed in, so no route ever renders for an unauthenticated caller. Toasts
// are provided app-wide (Block-not-implemented, delete/grant/revoke feedback).
import { QueryClientProvider, useQuery } from "@tanstack/react-query";
import { RouterProvider } from "@tanstack/react-router";
import { ToastProvider } from "@recruiter/shared";

import { SignInPanel } from "./components/sign-in-panel";
import { ErrorState, LoadingState } from "./components/states";
import { ApiError } from "./lib/api";
import { readLaunchSession } from "./lib/launch";
import { meQuery } from "./lib/queries";
import { queryClient } from "./lib/query-client";
import { resolveSignInReason } from "./lib/sign-in-reason";
import { LaunchKiosk } from "./pages/launch-kiosk";
import { router } from "./router";

// The launch-day TV path (pages/launch-kiosk.tsx). The kiosk machine has no
// Google sign-in, so /launch must be reachable without the identity boot: a
// stored remote-view session or a ?code= skips it outright, and a 401/403 on
// /launch falls into the kiosk's code form instead of the sign-in panel.
// Everyone signed in still gets /launch as an ordinary routed page.
function isLaunchPath(): boolean {
  return window.location.pathname.replace(/\/+$/, "") === "/launch";
}

function hasKioskCredential(): boolean {
  return new URLSearchParams(window.location.search).has("code") || readLaunchSession() !== null;
}

function Boot() {
  const kioskDirect = isLaunchPath() && hasKioskCredential();
  const me = useQuery({ ...meQuery, enabled: !kioskDirect });

  if (kioskDirect) return <LaunchKiosk />;

  if (me.isPending) {
    return (
      <div className="ad-boot">
        <LoadingState label="Signing in…" />
      </div>
    );
  }
  if (me.isError) {
    if (me.error instanceof ApiError) {
      // An unauthenticated visit to /launch is (almost certainly) the TV:
      // offer the code form, not a Google sign-in it cannot complete.
      if (isLaunchPath() && (me.error.status === 401 || me.error.status === 403)) {
        return <LaunchKiosk />;
      }
      if (me.error.status === 401 || me.error.status === 403) {
        return <SignInPanel reason={resolveSignInReason(me.error.status, me.error.code)} />;
      }
    }
    return (
      <div className="ad-boot">
        <ErrorState error={me.error} onRetry={() => void me.refetch()} />
      </div>
    );
  }
  // A stub-mode backend can answer 200 with `{email: null}` when no identity is
  // presented — treat that as not signed in.
  if (!me.data.email) return <SignInPanel />;

  return <RouterProvider router={router} />;
}

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ToastProvider>
        <Boot />
      </ToastProvider>
    </QueryClientProvider>
  );
}
